import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { Breadcrumbs, P, Spinner } from "AbstractElements";
import { Card, CardBody, Col } from "reactstrap";
import Moment from "moment";
import {
	clearRespondentDetail,
	fetchSurveyAnalyticsThunk,
	fetchSurveyRespondentDetailThunk,
	fetchSurveyRespondentsThunk,
	setDateFilters,
	setRespondentFilters,
} from "Features/Admin/Survey/surveyAnalyticsSlice";
import SurveyStatsCards from "./components/SurveyStatsCards";
import SurveyCharts from "./components/SurveyCharts";
import SurveyRespondentsTable from "./components/SurveyRespondentsTable";
import RespondentDetailModal from "./components/RespondentDetailModal";
import SurveyDateFilter from "./components/SurveyDateFilter";
import { formatDateParam, toApiDateFilters } from "./components/dateFilterUtils";

const toLocalDate = (value) => {
	if (!value) return null;
	const parsed = Moment(value, "YYYY-MM-DD", true);
	return parsed.isValid() ? parsed.toDate() : null;
};

const SurveyAnalytics = () => {
	const dispatch = useDispatch();
	const {
		analytics,
		respondents,
		pagination,
		respondentDetail,
		filters,
		isLoadingAnalytics,
		isLoadingRespondents,
		isLoadingDetail,
		error,
		detailError,
	} = useSelector((state) => state.surveyAnalytics, shallowEqual);

	const [isModalOpen, setIsModalOpen] = useState(false);

	const dateApiParams = useMemo(
		() =>
			toApiDateFilters({
				startDate: filters.start_date,
				endDate: filters.end_date,
			}),
		[filters.start_date, filters.end_date]
	);

	const respondentApiParams = useMemo(
		() => ({
			page: filters.page,
			per_page: filters.per_page,
			...dateApiParams,
		}),
		[filters.page, filters.per_page, dateApiParams]
	);

	// BE PR #419 respondents + PR #422 analytics: filter[start_date]/filter[end_date]
	useEffect(() => {
		dispatch(fetchSurveyAnalyticsThunk(dateApiParams));
	}, [dispatch, dateApiParams]);

	useEffect(() => {
		dispatch(fetchSurveyRespondentsThunk(respondentApiParams));
	}, [dispatch, respondentApiParams]);

	const handlePageChange = useCallback(
		(page) => {
			dispatch(setRespondentFilters({ page }));
		},
		[dispatch]
	);

	const handleDateFilterChange = useCallback(
		({ preset, year, startDate, endDate }) => {
			dispatch(
				setDateFilters({
					preset,
					year,
					start_date: formatDateParam(startDate),
					end_date: formatDateParam(endDate),
				})
			);
		},
		[dispatch]
	);

	const handleRowClick = useCallback(
		(row) => {
			if (!row?.userId) return;
			setIsModalOpen(true);
			dispatch(fetchSurveyRespondentDetailThunk(row.userId));
		},
		[dispatch]
	);

	const handleCloseModal = useCallback(() => {
		setIsModalOpen(false);
		dispatch(clearRespondentDetail());
	}, [dispatch]);

	const isInitialLoading = isLoadingAnalytics && !analytics;
	const startDateValue = toLocalDate(filters.start_date);
	const endDateValue = toLocalDate(filters.end_date);
	const hasDateFilter = Boolean(filters.start_date || filters.end_date);

	return (
		<Fragment>
			<Breadcrumbs
				mainTitle="Survey Analytics"
				parent="Admin"
				title="Survey Analytics"
			/>

			<Col lg="12">
				<Card
					className="border-0 shadow-sm overflow-hidden"
					style={{
						borderRadius: "1.5rem",
						background: "linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)",
					}}
				>
					<CardBody className="p-3 p-md-4">
						<div className="mb-4">
							<h4 className="fw-semibold mb-1">Dashboard Survey Aplikasi</h4>
							<P className="mb-0 text-muted">
								Analitik jawaban Application Survey dari pengguna Brofesional App.
							</P>
						</div>

						<SurveyDateFilter
							preset={filters.preset || "all"}
							year={filters.year}
							startDate={startDateValue}
							endDate={endDateValue}
							onChange={handleDateFilterChange}
						/>

						{isInitialLoading && (
							<div className="loader-box">
								<Spinner attrSpinner={{ className: "loader-30" }} />
							</div>
						)}

						{error && (
							<div className="rounded-4 border border-danger-subtle bg-danger-subtle p-3 mb-3">
								<P className="mb-0 text-danger">
									{typeof error === "string" ? error : "Terjadi kesalahan saat memuat data."}
								</P>
							</div>
						)}

						{!isInitialLoading && analytics && (
							<>
								{analytics.totalRespondents === 0 ? (
									<div className="rounded-4 border border-dashed bg-light p-4 text-center text-muted mb-4">
										Belum ada responden yang menyelesaikan survey lengkap
										{hasDateFilter ? " pada periode yang dipilih." : "."}
									</div>
								) : (
									<>
										<SurveyStatsCards analytics={analytics} />
										<SurveyCharts analytics={analytics} />
									</>
								)}

								<SurveyRespondentsTable
									respondents={respondents}
									pagination={pagination}
									isLoading={isLoadingRespondents}
									hasDateFilter={hasDateFilter}
									onRowClick={handleRowClick}
									onPageChange={handlePageChange}
								/>
							</>
						)}
					</CardBody>
				</Card>
			</Col>

			<RespondentDetailModal
				isOpen={isModalOpen}
				onClose={handleCloseModal}
				detail={respondentDetail}
				isLoading={isLoadingDetail}
				error={detailError}
			/>
		</Fragment>
	);
};

export default SurveyAnalytics;
